import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {defineMessages} from 'react-intl';
import {CSSTransitionGroup as ReactCSSTransitionGroup} from 'react-transition-group';
import {inject} from 'mobx-react';
import {injectIntl} from 'react-intl';

//lodash
import invoke from 'lodash/invoke';
import find from 'lodash/find';
import get from 'lodash/get';

//helpers
import {richTextHasValue} from 'utils/string-utils';
import {stepNoteTypes, getCustomIconLabel} from 'utils/instruction-utils';

//components
import CloudinaryPictureWithControls from 'components/CloudinaryPictureWithControls';
import ChecklistIndicatorIcons from 'components/ChecklistIndicatorIcons';

import ToolbarItem from './ToolbarItem';

//styled-components
import {
  Content,
  Fader,
  ImageWrapper,
  StyledFadingOverflow,
  Toolbar,
  ToolbarWrapper,
  ToolContent,
  StyledNoteHeader,
  ChecklistIconWrapper
} from './styles';

const messages = defineMessages({
  tip: {
    id: 'toolbar_title_tip',
    defaultMessage: 'Tip'
  },
  warning: {
    id: 'toolbar_title_warning',
    defaultMessage: 'Warning'
  },
  custom: {
    id: 'toolbar_title_custom',
    defaultMessage: 'Alternative'
  },
  fix: {
    id: 'toolbar_title_fix',
    defaultMessage: 'Fix'
  }
});

@inject('store')
class ToolbarComponent extends Component {
  static defaultProps = {
    notes: [],
    imageUrls: {},
    metadata: {},
    shouldAllToolbarItemsBeEnabled: false
  };

  static propTypes = {
    getToolbarButtonStyle: PropTypes.func,
    imageSizes: PropTypes.string,
    isCheck: PropTypes.bool.isRequired,
    onToggle: PropTypes.func,
    selectedType: PropTypes.string,
    shouldShowSpinnerWhileLoading: PropTypes.bool
  };

  handleBackgroundClick = () => {
    this.propagateToggle({type: null, trackAction: 'closeHintByBackground'});
  };

  handleHintToggle = ({type}) => {
    const {selectedType} = this.props;

    if (type === selectedType) {
      this.propagateToggle({type: null, trackAction: 'closeHintByButton'});
    } else {
      this.propagateToggle({type});
    }
  };

  propagateToggle = ({type, trackAction}) => {
    invoke(this.props, 'onToggle', {type, trackAction});
  };

  shouldToolbarItemBeEnabled = key => {
    const {notes, shouldAllToolbarItemsBeEnabled, store, noteContentKey} = this.props;
    const {platform} = store;
    const {canUseMediaInStepHints} = platform;

    const noteContent = find(notes, {iconId: key});
    const hasContent = richTextHasValue(get(noteContent, noteContentKey));
    const hasImageUrl = canUseMediaInStepHints && get(noteContent, 'media.url');

    return shouldAllToolbarItemsBeEnabled || hasContent || hasImageUrl;
  };

  selectedNote = iconId => find(this.props.notes, {iconId});

  getIconLabel = icon => {
    const {
      intl: {formatMessage},
      store: {
        platform: {currentLanguage}
      }
    } = this.props;
    const {type, labelTranslations = []} = icon;

    if (type !== stepNoteTypes.CUSTOM || !labelTranslations.length) {
      return formatMessage(messages[type.toLowerCase()]);
    }

    return getCustomIconLabel(labelTranslations, currentLanguage);
  };

  render() {
    const {
      getToolbarButtonStyle,
      hintContent,
      imageSizes,
      isCheck,
      selectedType,
      shouldShowSpinnerWhileLoading,
      store,
      noteContentKey
    } = this.props;
    const {app, platform} = store;
    const {
      theme: {icons}
    } = app;
    const {canUseMediaInStepHints, showStepNoteIconLabel} = platform;

    const selectedNote = this.selectedNote(selectedType);
    const selectedIcon = find(icons, {id: selectedType});
    const selectedContent = get(selectedNote, noteContentKey);
    const selectedImageUrl = get(selectedNote, 'media.url');
    const selectedMetadata = get(selectedNote, 'media.metadata');
    const selectedNoteIsWarning = get(selectedIcon, 'type') === stepNoteTypes.WARNING;

    return (
      <ToolbarWrapper>
        {hintContent ? (
          hintContent
        ) : (
          <ReactCSSTransitionGroup transitionName="example" transitionEnterTimeout={150} transitionLeaveTimeout={150}>
            {selectedType && (
              <ToolContent>
                <Fader className="fader" onClick={this.handleBackgroundClick} />
                <StyledFadingOverflow>
                  {selectedNoteIsWarning && <StyledNoteHeader>{this.getIconLabel(selectedIcon)}</StyledNoteHeader>}
                  <Content className="content">
                    {canUseMediaInStepHints && selectedImageUrl && (
                      <ImageWrapper>
                        <CloudinaryPictureWithControls
                          aspectRatio={4 / 3}
                          src={selectedImageUrl}
                          metadata={selectedMetadata}
                          sizes={imageSizes}
                          shouldShowSpinnerWhileLoading={shouldShowSpinnerWhileLoading}
                          shouldShowOverlayShapes
                        />
                      </ImageWrapper>
                    )}
                    <div data-cy="hint-content" dangerouslySetInnerHTML={{__html: selectedContent}} />
                  </Content>
                </StyledFadingOverflow>
              </ToolContent>
            )}
          </ReactCSSTransitionGroup>
        )}
        {isCheck ? (
          <ChecklistIconWrapper>
            <ChecklistIndicatorIcons />
          </ChecklistIconWrapper>
        ) : (
          <Toolbar>
            {icons.map(icon => (
              <ToolbarItem
                key={icon.id}
                type={icon.type.toLowerCase()}
                iconId={icon.id}
                onToggle={this.handleHintToggle}
                isActive={selectedType === icon.id}
                label={showStepNoteIconLabel && this.getIconLabel(icon)}
                themeIconUrl={icon.svg}
                isDisabled={!this.shouldToolbarItemBeEnabled(icon.id)}
                getToolbarButtonStyle={getToolbarButtonStyle}
                isWarning={icon.type === stepNoteTypes.WARNING}
              />
            ))}
          </Toolbar>
        )}
      </ToolbarWrapper>
    );
  }
}

export default injectIntl(ToolbarComponent);
